/**
 * Jovii — the 24 avatars, drawn.
 *
 * The server stores an ID from a fixed set and nothing else: no upload path, no image
 * store, no moderation problem, no bytes on the wire (docs/SOCIAL.md § "Profiles and
 * avatars"). Which means the client has to draw them, and this is where.
 *
 * WHAT THESE ARE. One 24×24 mark per id, stroked, no fills, so a single glyph reads at
 * 28 px in a friend row and at 96 px on the profile screen without a second asset. They
 * are ABSTRACT rather than illustrative on purpose: twenty-four recognisable animals
 * would be twenty-four drawings to maintain, and the job here is only that no two people
 * in one household pick marks they cannot tell apart. The picker shows the name under
 * each, so the id is never something you have to infer from the picture.
 *
 * KEEP THIS TABLE IN STEP WITH `AVATARS` IN `protocol.ts`, which is append-only for the
 * reason stated there: a stored id that vanishes leaves somebody blank. `markFor()`
 * falls back rather than throwing, so a server that grows a 25th avatar before this file
 * does still renders every person.
 */

import { AVATARS, type AvatarId } from './protocol';

/** Every mark is a list of stroked paths in a 0 0 24 24 box. Circles are written as arc
 *  pairs so one `<path>` element covers every case. */
export type Mark = readonly string[];

const MARKS: Record<string, Mark> = {
  /* ── creatures ─────────────────────────────────────────────────────────── */
  // an otter: a small head and a long back curling into the water
  otter: ['M13 4.5a3 3 0 1 0 6 0a3 3 0 1 0-6 0', 'M13.6 9.9C11 12.4 8.6 12.6 6.6 14.7 4.4 17 4.9 20 7.5 20'],
  // a fox: one triangle, two ears
  fox: ['M12 20 4.5 8.5 7.5 4.5 12 8l4.5-3.5 3 4Z'],
  // an owl: two eyes in a squared face
  owl: ['M5 8a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v6a7 7 0 0 1-14 0Z', 'M8.6 9.5a1.6 1.6 0 1 0 3.2 0a1.6 1.6 0 1 0-3.2 0', 'M13.2 9.5a1.6 1.6 0 1 0 3.2 0a1.6 1.6 0 1 0-3.2 0'],
  // a moth: a body and two wings
  moth: ['M12 5.5v13', 'M12 9c-4-4.5-8.5-1.5-6.3 3.4C7 15.8 10.6 16.3 12 14.2', 'M12 9c4-4.5 8.5-1.5 6.3 3.4C17 15.8 13.4 16.3 12 14.2'],
  // a crane: a long neck and a beak
  crane: ['M5.5 19.5c1.5-5.5 5.5-8 10.5-8', 'M16 11.5 18.5 6', 'M18.5 6l-3.2.9'],
  // an ibex: two swept horns
  ibex: ['M9.5 20c-2.5-6.5-3.5-12 .5-14.5', 'M14.5 20c2.5-6.5 3.5-12-.5-14.5', 'M10.5 12.5h3'],
  // a lynx: tufted ears over a round face
  lynx: ['M7.5 8.5 5.5 3.5 9.5 6', 'M16.5 8.5 18.5 3.5 14.5 6', 'M7 13.5a5 5 0 1 0 10 0a5 5 0 1 0-10 0'],
  // a newt: a curled tail
  newt: ['M13.5 5.5a6.5 6.5 0 1 1-6.2 8.4', 'M13.5 11.5a3.5 3.5 0 1 0-3.4 4.4'],

  /* ── sky and stone ─────────────────────────────────────────────────────── */
  moon: ['M16.5 3.5a9 9 0 1 0 3.5 12.8A10 10 0 0 1 16.5 3.5Z'],
  comet: ['M13.5 7a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0-7 0', 'M13.8 9.7 5 18.5', 'M10.5 10.5 6 12.5', 'M13.5 14 11.5 18.5'],
  orbit: ['M8.6 12a3.4 3.4 0 1 0 6.8 0a3.4 3.4 0 1 0-6.8 0', 'M4.6 8.2C2.4 11 2.2 14.2 4.2 16.2c2.6 2.6 8 1.2 12-3s5.6-9.4 3-12c-2-2-5.2-1.8-8 .4'],
  prism: ['M12 3.5 20.5 19H3.5Z', 'M3 8.5 9.5 12', 'M14.5 12 21.5 15.5'],
  ember: ['M12 20.5c-3.6 0-6-2.6-6-5.8C6 10.5 12 9.5 11 3.5c3.4 2.2 7 5.6 7 11.2 0 3.2-2.4 5.8-6 5.8Z', 'M12 20.5c-1.7 0-2.8-1.2-2.8-2.7 0-2 2.8-2.6 2.3-5.3 1.6 1 3.3 2.6 3.3 5.3 0 1.5-1.1 2.7-2.8 2.7Z'],
  frost: ['M12 3v18', 'M4.2 7.5 19.8 16.5', 'M4.2 16.5 19.8 7.5', 'M12 6.5 9.5 5', 'M12 6.5 14.5 5', 'M12 17.5 9.5 19', 'M12 17.5 14.5 19'],
  tide: ['M2.5 8.5q3.2-3 6.4 0t6.4 0 6.2 0', 'M2.5 13q3.2-3 6.4 0t6.4 0 6.2 0', 'M2.5 17.5q3.2-3 6.4 0t6.4 0 6.2 0'],
  dune: ['M2 18.5q5-9 10-3t10-5', 'M2 21h20'],
  quartz: ['M12 2.5 19 7v10l-7 4.5L5 17V7Z', 'M12 2.5v19', 'M5 7l7 4.5L19 7'],

  /* ── growing things, and the stones named after them ───────────────────── */
  fern: ['M12 21C12 12 14 6 19 3', 'M12.8 16.5c2.4.6 4.2-.4 5-2.6', 'M14.2 12.2c2.3.4 3.9-.7 4.5-2.9', 'M16 8.2c2 .1 3.2-.9 3.6-2.8'],
  clove: ['M12 21V9', 'M12 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z', 'M12 12.5 8 10', 'M12 12.5 16 10'],
  plum: ['M7 13.5a5 5 0 1 0 10 0a5 5 0 1 0-10 0', 'M12 8.5V13', 'M12.5 8.5C13 5.5 15 4 18 3.8'],
  thyme: ['M12 21V4.5', 'M12 8.5c-2.4 0-3.6-1.2-3.6-3.4 2.4 0 3.6 1.2 3.6 3.4Z', 'M12 8.5c2.4 0 3.6-1.2 3.6-3.4-2.4 0-3.6 1.2-3.6 3.4Z', 'M12 14.5c-2.4 0-3.6-1.2-3.6-3.4 2.4 0 3.6 1.2 3.6 3.4Z', 'M12 14.5c2.4 0 3.6-1.2 3.6-3.4-2.4 0-3.6 1.2-3.6 3.4Z'],
  onyx: ['M12 3 20 12l-8 9-8-9Z', 'M8.5 12 12 8l3.5 4'],
  amber: ['M12 3.5c4.2 4.8 6.3 8.4 6.3 11a6.3 6.3 0 1 1-12.6 0c0-2.6 2.1-6.2 6.3-11Z', 'M10.2 14.5a1.9 1.9 0 1 0 3.8 0a1.9 1.9 0 1 0-3.8 0'],
  jade: ['M3.5 12a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0-17 0', 'M9 12a3 3 0 1 0 6 0a3 3 0 1 0-6 0']
};

/** The mark for an id, or a plain disc for an id this build has never heard of. */
export function markFor(avatar: string | undefined | null): Mark {
  const found = avatar === null || avatar === undefined ? undefined : MARKS[avatar];
  return found ?? ['M5.5 12a6.5 6.5 0 1 0 13 0a6.5 6.5 0 1 0-13 0'];
}

/** True when this build can actually draw that id. Used by the picker so it never shows
 *  an empty tile, and by the test that keeps this table honest. */
export function hasMark(avatar: string): boolean {
  return Object.prototype.hasOwnProperty.call(MARKS, avatar);
}

/** Every id this build draws, in the server's order. */
export const DRAWN: readonly AvatarId[] = AVATARS.filter((a) => hasMark(a));
